import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { supabase } from '../../lib/supabase';
import BlogHero from './components/BlogHero';
import FeaturedPosts from './components/FeaturedPosts';
import CategoryFilter from './components/CategoryFilter';
import ArticleGrid from './components/ArticleGrid';
import LandingFooter from '../nord-pass-landing-page/components/LandingFooter';

const BlogNewsPage = () => {
  const [allPosts, setAllPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [featuredPosts, setFeaturedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState('Alle');
  const [activeDateFilter, setActiveDateFilter] = useState('all');

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error: fetchError } = await supabase?.from('blog_posts')?.select('*')?.order('published_at', { ascending: false });

      if (fetchError) throw fetchError;

      const posts = data || [];
      setAllPosts(posts);
      setFeaturedPosts(posts?.filter(p => p?.is_featured)?.slice(0, 2));
      setFilteredPosts(posts);
    } catch (err) {
      setError(err?.message || 'Kunne ikke laste artikler');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (posts, category, dateFilter) => {
    let result = [...(posts || [])];

    if (category && category !== 'Alle') {
      result = result?.filter(p => p?.category === category);
    }

    const now = new Date();
    if (dateFilter === 'month') {
      const cutoff = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
      result = result?.filter(p => new Date(p?.published_at) >= cutoff);
    } else if (dateFilter === '3months') {
      const cutoff = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
      result = result?.filter(p => new Date(p?.published_at) >= cutoff);
    } else if (dateFilter === 'year') {
      const cutoff = new Date(now.getFullYear(), 0, 1);
      result = result?.filter(p => new Date(p?.published_at) >= cutoff);
    }

    return result;
  };

  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    setFilteredPosts(applyFilters(allPosts, category, activeDateFilter));
  };

  const handleDateFilterChange = (dateFilter) => {
    setActiveDateFilter(dateFilter);
    setFilteredPosts(applyFilters(allPosts, activeCategory, dateFilter));
  };

  return (
    <>
      <Helmet>
        <title>Blogg & Nyheter — NordPass</title>
        <meta name="description" content="Siste nyheter, tips og innsikt om norske veier, trafikk, bompenger og kjøretøy fra NordPass redaksjonen." />
        <link
          href="https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=Lora:ital,wght@0,400;0,700;1,400;1,700&family=Inter:wght@300;400;500;600&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <div style={{ background: '#050A12', minHeight: '100vh', overflowX: 'hidden' }}>
        {/* Navigation */}
        <nav
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 100,
            background: 'rgba(5,10,18,0.95)',
            backdropFilter: 'blur(16px)',
            borderBottom: '1px solid rgba(255,255,255,0.06)'
          }}
        >
          <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 5%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '72px' }}>
            <a href="/nord-pass-landing-page" style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none' }}>
              <div style={{ width: '32px', height: '32px', background: '#3B6FD4', clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))' }} />
              <span style={{ fontFamily: 'Syne, sans-serif', fontSize: '1.125rem', fontWeight: 800, color: '#EEF0F2', letterSpacing: '-0.03em' }}>NordPass</span>
            </a>
            <div style={{ display: 'flex', alignItems: 'center', gap: '32px' }}>
              {[
                { label: 'Hjem', href: '/nord-pass-landing-page' },
                { label: 'Tjenester', href: '/tjenester-services' },
                { label: 'Om Oss', href: '/om-oss-about-us' },
                { label: 'Blogg', href: '/blog-news-landing-page-blogg-nyheter' },
                { label: 'Kontakt', href: '/nord-pass-contact-page' }
              ]?.map((link, i) => (
                <a
                  key={i}
                  href={link?.href}
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.875rem',
                    color: link?.href?.includes('blog') ? '#EEF0F2' : 'rgba(238,240,242,0.65)',
                    textDecoration: 'none',
                    borderBottom: link?.href?.includes('blog') ? '2px solid #3B6FD4' : '2px solid transparent',
                    paddingBottom: '2px',
                    transition: 'color 150ms ease'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.color = '#EEF0F2'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = link?.href?.includes('blog') ? '#EEF0F2' : 'rgba(238,240,242,0.65)'; }}
                >
                  {link?.label}
                </a>
              ))}
            </div>
            <a
              href="/login"
              style={{ fontFamily: 'Syne, sans-serif', fontSize: '0.8125rem', fontWeight: 700, color: '#EEF0F2', textDecoration: 'none', padding: '10px 20px', background: '#3B6FD4', letterSpacing: '0.06em', textTransform: 'uppercase' }}
            >
              Logg inn
            </a>
          </div>
        </nav>

        <BlogHero />

        {!loading && featuredPosts?.length > 0 && (
          <FeaturedPosts posts={featuredPosts} />
        )}

        <CategoryFilter
          activeCategory={activeCategory}
          onCategoryChange={handleCategoryChange}
          activeDateFilter={activeDateFilter}
          onDateFilterChange={handleDateFilterChange}
          totalCount={filteredPosts?.length}
        />

        <ArticleGrid
          posts={filteredPosts}
          loading={loading}
          error={error}
        />

        <LandingFooter />
      </div>
    </>
  );
};

export default BlogNewsPage;
